import { FormProvider, useForm } from "react-hook-form";
import { Header } from "../../components/Header";
import { Box, Button, Flex, Portal, Stack, Text, useDisclosure } from "@chakra-ui/react";
import { Limites } from "../Pdv/LImites";
import { Actions } from "./Actions";
import { Emissor } from "./Emissor";
import { Pedido } from "./Pedido";
import { Navigate, useParams } from "react-router-dom";
import { usePedido } from "../../hooks/pagamento/usePedido";
import { Loading } from "../../components/Loading";
import { ModalCancelar } from "./ModalCancelar";
import { Bilhetes } from "./Bilhetes";
import { PagamentoDinheiro } from "./PagamentoDinheiro";
import { PagamentoPix } from "./PagamentoPix";
import { Footer } from "../../components/Footer";
import { PagamentoCartao } from "./PagamentoCartao";
import { PedidoStatusCode } from "../../utils/interfaces";
import { useOrdemTrabalho } from "../../hooks/pagamento/useOrdemTrabalho";
import { getUser, isEmissor, isRepresentante, MENSAGEM_PENDENCIA_FINANCEIRA_TOPO, refreshUser, temPendenciaFinanceira } from "../../libs/auth";
import Swal from "sweetalert2";
import { getReservaDeadline, ReservaCountdown, useReservaCountdown } from "./ReservaCountdown";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../libs/axios";
import { AxiosErrorResponse } from "../../hooks/pdv/mutationReserva";
import { deveBloquearDevolucaoNoPedido, deveBloquearDevolucaoPresencialNoPedido } from "../../utils/devolucao";
import { useCheckoutStatus } from "../../hooks/pagamento/useCheckoutStatus";
import { CheckoutProgressPanel, CheckoutStayOnPageWarning } from "../../components/CheckoutProgress";

const MENSAGEM_AVISO_DEVOLUCAO = 'Atenção: ao realizar o pagamento deste pedido, não será possível devolver, pois o embarque ocorre em menos de 3 horas.';
const MENSAGEM_PENDENCIA_FINANCEIRA = MENSAGEM_PENDENCIA_FINANCEIRA_TOPO;

function resolverPendenciaFinanceira(agencia?: { status?: unknown; pendencia_financeira?: unknown } | null) {
  if (!agencia || (agencia.status === undefined && agencia.pendencia_financeira === undefined)) {
    return undefined;
  }

  return Boolean(agencia.pendencia_financeira || Number(agencia.status) === 3);
}

export function Pagamento() {
  const form = useForm();
  const { id } = useParams() as { id: string };
  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
    isRestoringPedido,
    isPedidoPageVisible,
    pedidoValidationFailed,
    retryPedidoValidation,
  } = usePedido({ id, revalidateOnHistoryRestore: true });
  const { updateOrdemTrabalho, isUpdating } = useOrdemTrabalho();
  const user = getUser();
  const representanteOnly = isRepresentante();
  const emissorOnly = isEmissor();
  const pedido = data?.pedido;
  const hasValidPedido = pedido !== undefined && pedido !== null;
  const pendenciaFinanceiraAtual = resolverPendenciaFinanceira(data?.emissor);
  const pendenciaFinanceira = pendenciaFinanceiraAtual ?? temPendenciaFinanceira();
  const reservaDeadline = getReservaDeadline(data?.bilhetes);
  const reservaCountdown = useReservaCountdown(reservaDeadline);
  const [isAutoCancelling, setIsAutoCancelling] = useState(false);
  const autoCancelTriggeredRef = useRef(false);
  const processingRedirectHandledRef = useRef(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenDinheiro,
    onOpen: onOpenDinheiro,
    onClose: onCloseDinheiro,
  } = useDisclosure();
  const {
    isOpen: isOpenPix,
    onOpen: onOpenPix,
    onClose: onClosePix,
  } = useDisclosure();

  const {
    isOpen: isOpenCartao,
    onOpen: onOpenCartao,
    onClose: onCloseCartao,
  } = useDisclosure();

  const userAgenciaId = user?.agencia?.id;
  const pedidoAgenciaId = pedido?.agencia_id;
  const userId = user?.id;
  const pedidoUsuarioId = pedido?.usuario_id;
  const representantePodeCancelar = !representanteOnly
    || (pedidoAgenciaId !== undefined
      && userAgenciaId !== undefined
      && String(pedidoAgenciaId) === String(userAgenciaId));
  const emissorPodeCancelar = !emissorOnly
    || (pedidoUsuarioId !== undefined
      && userId !== undefined
      && String(pedidoUsuarioId) === String(userId));
  const canCancelPedido = representantePodeCancelar && emissorPodeCancelar;
  const pedidoDisponivelParaCheckout = hasValidPedido && [
    PedidoStatusCode.Aberto,
    PedidoStatusCode.Reservado,
  ].includes(pedido.status);
  const pedidoEmProcessamento = hasValidPedido && pedido.status === PedidoStatusCode.Processando;
  const checkoutEffectsAllowed =
    !isLoading &&
    !isFetching &&
    !isError &&
    !isRestoringPedido &&
    isPedidoPageVisible &&
    !pedidoValidationFailed &&
    pedidoDisponivelParaCheckout;
  const { data: processingCheckoutProgress } = useCheckoutStatus(
    id,
    pedidoEmProcessamento && isPedidoPageVisible && !pedidoValidationFailed
  );

  const atualizarPerfilEPedido = useCallback(async () => {
    await refreshUser().catch(() => undefined);
    return refetch();
  }, [refetch]);

  useEffect(() => {
    void atualizarPerfilEPedido();

    const handleFocus = () => {
      void atualizarPerfilEPedido();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void atualizarPerfilEPedido();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [atualizarPerfilEPedido]);

  useEffect(() => {
    if (!pendenciaFinanceira) {
      return;
    }

    const interval = window.setInterval(() => {
      void atualizarPerfilEPedido();
    }, 30000);

    return () => window.clearInterval(interval);
  }, [atualizarPerfilEPedido, pendenciaFinanceira]);

  useEffect(() => {
    if (!pedidoEmProcessamento || !processingCheckoutProgress || processingRedirectHandledRef.current) {
      return;
    }

    if (processingCheckoutProgress.status === "completed") {
      processingRedirectHandledRef.current = true;
      window.location.assign(processingCheckoutProgress.redirect_to || `/finalizada/${id}`);
      return;
    }

    if (processingCheckoutProgress.status === "failed") {
      processingRedirectHandledRef.current = true;
      const message = processingCheckoutProgress.error_message
        || processingCheckoutProgress.description
        || "Não foi possível concluir o pedido.";

      Swal.fire({
        icon: "error",
        title: "Erro ao finalizar o pedido",
        text: message,
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => {
        window.location.assign(processingCheckoutProgress.redirect_to || "/");
      });
    }
  }, [id, pedidoEmProcessamento, processingCheckoutProgress]);

  useEffect(() => {
    const reservaExpirouDeFato = reservaDeadline !== null && reservaDeadline.getTime() <= Date.now();

    if (!checkoutEffectsAllowed || !pedidoDisponivelParaCheckout || !reservaCountdown.isExpired || !reservaExpirouDeFato || !pedido?.id || autoCancelTriggeredRef.current || !canCancelPedido) {
      return;
    }

    autoCancelTriggeredRef.current = true;

    const cancelarPedidoExpirado = async () => {
      setIsAutoCancelling(true);

      try {
        await api.delete(`/pedido/${pedido.id}/cancelar-por-expiracao`);

        onClose();
        onCloseDinheiro();
        onClosePix();
        onCloseCartao();

        await Swal.fire({
          icon: "info",
          title: "Reserva expirada",
          text: "O prazo da reserva terminou e o pedido foi cancelado automaticamente.",
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        window.location.assign("/");
      } catch (err: unknown) {
        const error = err as AxiosErrorResponse;
        const redirectTo = error.response?.data?.data?.redirect_to
          || error.response?.data?.redirect_to;
        const errorCode = error.response?.data?.data?.code
          || error.response?.data?.code;

        if (error.response?.status === 409) {
          if (errorCode === 'reserva_vigente') {
            window.setTimeout(() => {
              autoCancelTriggeredRef.current = false;
              void refetch();
            }, 1000);
            return;
          }

          onClose();
          onCloseDinheiro();
          onClosePix();
          onCloseCartao();

          const pedidoFinalizado = errorCode === 'pedido_nao_disponivel'
            && redirectTo?.includes('/finalizada/');
          const possuiPagamentoOuConfirmacao = [
            'checkout_em_processamento',
            'pagamento_registrado',
            'bilhete_confirmado',
          ].includes(errorCode ?? '');

          if (!pedidoFinalizado && !possuiPagamentoOuConfirmacao) {
            const message =
              error.response?.data?.data?.message
              || error.response?.data?.message
              || "Não foi possível confirmar o cancelamento automático do pedido.";

            await Swal.fire({
              icon: "warning",
              title: "Não foi possível cancelar o pedido",
              text: message,
              confirmButtonText: "Ver pedido",
              allowOutsideClick: false,
              allowEscapeKey: false,
            });

            window.location.replace(redirectTo || `/pedido/${pedido.id}`);
            return;
          }

          await Swal.fire({
            icon: "info",
            title: pedidoFinalizado ? "Pedido finalizado" : "Prazo da reserva encerrado",
            text: pedidoFinalizado
              ? "Este pedido já foi finalizado. Você será direcionado para a confirmação."
              : "O prazo da reserva terminou, mas o pedido possui pagamento ou confirmação vinculada. Verifique os detalhes do pedido antes de realizar qualquer nova ação.",
            confirmButtonText: "Ver pedido",
            allowOutsideClick: false,
            allowEscapeKey: false,
          });

          window.location.replace(
            pedidoFinalizado && redirectTo
              ? redirectTo
              : `/pedido/${pedido.id}`
          );

          return;
        }

        const message =
          error.response?.data?.data?.message ||
          error.response?.data?.message ||
          "A reserva expirou, mas não foi possível cancelar o pedido automaticamente.";

        await Swal.fire({
          icon: "warning",
          title: "Reserva expirada",
          text: message,
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        window.location.assign("/");
      } finally {
        setIsAutoCancelling(false);
      }
    };

    cancelarPedidoExpirado();
  }, [
    reservaDeadline,
    reservaCountdown.isExpired,
    pedido?.id,
    pedido?.status,
    canCancelPedido,
    checkoutEffectsAllowed,
    pedidoDisponivelParaCheckout,
    onClose,
    onCloseCartao,
    onCloseDinheiro,
    onClosePix,
    refetch,
  ]);

  if (isLoading || isRestoringPedido || (!hasValidPedido && isFetching)) return <Loading />;

  if (isError || pedidoValidationFailed || !data || !pedido) {
    return (
      <Flex minH="60vh" alignItems="center" justifyContent="center">
        <Box bg="white" borderWidth={1} borderColor="gray.200" p={6} maxW="420px" textAlign="center">
          <Text fontSize="lg" fontWeight="bold" mb={2}>
            Não foi possível atualizar o status do pedido.
          </Text>
          <Text color="gray.600" mb={4}>
            Verifique sua conexão e tente novamente antes de continuar.
          </Text>
          <Button colorScheme="blue" onClick={() => void retryPedidoValidation()}>
            Tentar novamente
          </Button>
        </Box>
      </Flex>
    );
  }

  if (pedido.status === PedidoStatusCode.Finalizado) {
    return <Navigate to={`/finalizada/${pedido.id}`} replace />;
  }

  if (pedidoEmProcessamento) {
    return (
      <FormProvider {...form}>
        <Box
          display={"flex"}
          flexDir={"column"}
          justifyContent={"center"}
          width={"full"}
          alignItems={"center"}
        >
          <Flex
            maxWidth={"140rem"}
            width={"100%"}
            flexDir={"column"}
            justifyContent={"center"}
          >
            <Header />
            <Flex minH="60vh" alignItems="center" justifyContent="center" px={4} py={10} bg="white">
              <Stack spacing={6} w="100%" maxW="1100px">
                <CheckoutStayOnPageWarning />
                <CheckoutProgressPanel
                  progress={processingCheckoutProgress}
                  fallbackStep="confirming_order"
                  method={processingCheckoutProgress?.method ?? "desconhecido"}
                />
                <Box bg="purple.50" border="1px solid" borderColor="purple.100" borderRadius="xl" px={5} py={4}>
                  <Text fontWeight="700" color="purple.800">
                    Pedido em processamento
                  </Text>
                  <Text mt={1} fontSize="sm" color="gray.700">
                    O pagamento já foi registrado e estamos aguardando a confirmação final do fornecedor. Esta tela será atualizada automaticamente.
                  </Text>
                </Box>
              </Stack>
            </Flex>
            <Footer />
          </Flex>
        </Box>
      </FormProvider>
    );
  }

  if (!pedidoDisponivelParaCheckout) {
    return <Navigate to="/" replace />;
  }

  const bloqueioDevolucaoAtivo = deveBloquearDevolucaoNoPedido(data.bilhetes);
  const bloqueioDevolucaoPresencial = pedido.devolucao_presencial
    || deveBloquearDevolucaoPresencialNoPedido(data.bilhetes);
  const exibirAvisoPagamentoSemDevolucao = async () => {
    const mensagens = [
      bloqueioDevolucaoAtivo ? MENSAGEM_AVISO_DEVOLUCAO : null,
    ].filter((mensagem): mensagem is string => typeof mensagem === 'string' && mensagem.trim() !== '');

    if (mensagens.length === 0) {
      return;
    }

    await Swal.fire({
      toast: false,
      position: 'center',
      showConfirmButton: true,
      icon: 'warning',
      title: 'Atenção',
      text: mensagens.join('\n'),
    });
  };

  const onCancel = () => {
    onOpen();
  };

  const confirmarFormaPagamento = async (
    formaPagamento: 'dinheiro' | 'cartao' | 'pix' | 'link' | 'pos',
    formaPagamentoAtual?: string | number | null,
  ) => {
    const codigosFormaPagamento = {
      dinheiro: 1,
      cartao: 2,
      pix: 3,
      link: 4,
      pos: 5,
    } as const;
    const formaPagamentoPedido = formaPagamentoAtual === null || formaPagamentoAtual === undefined
      ? null
      : String(formaPagamentoAtual).toLowerCase();

    if (
      formaPagamentoPedido === formaPagamento
      || formaPagamentoPedido === String(codigosFormaPagamento[formaPagamento])
    ) {
      return true;
    }

    const confirmacao = await Swal.fire({
      icon: 'warning',
      title: 'Atenção!',
      html: [
        '<p>Tem certeza da opção de pagamento?</p>',
        '<p>O sistema não permitirá a troca de opção de pagamento no mesmo pedido.</p>',
      ].join(''),
      showCancelButton: true,
      confirmButtonText: 'Concordar',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
      allowOutsideClick: false,
    });

    if (!confirmacao.isConfirmed) {
      return false;
    }

    try {
      await api.post(`/checkout/forma-pagamento/${pedido.id}`, {
        forma_pagamento: formaPagamento,
      });
      await refetch();
      return true;
    } catch (err: unknown) {
      const error = err as AxiosErrorResponse;
      await Swal.fire({
        icon: 'error',
        title: 'Não foi possível registrar a forma de pagamento',
        text: error.response?.data?.message
          || 'Atualize o pedido e tente novamente.',
        confirmButtonText: 'OK',
      });
      await refetch();
      return false;
    }
  };

  const prepararOperacaoPagamento = async (formaPagamento: 'dinheiro' | 'cartao' | 'pix' | 'link' | 'pos') => {
    const resultado = await atualizarPerfilEPedido();
    const pendenciaAtual = resolverPendenciaFinanceira(resultado.data?.emissor) ?? temPendenciaFinanceira();

    if (pendenciaAtual) {
      await Swal.fire({
        icon: 'warning',
        title: 'Pendência financeira',
        text: MENSAGEM_PENDENCIA_FINANCEIRA,
        confirmButtonText: 'OK',
      });

      return false;
    }

    if (!await confirmarFormaPagamento(formaPagamento, resultado.data?.pedido?.forma_pagamento ?? pedido.forma_pagamento)) {
      return false;
    }

    await exibirAvisoPagamentoSemDevolucao();

    return true;
  };

  const onDinheiro = async () => {
    if (!await prepararOperacaoPagamento('dinheiro')) {
      return;
    }

    onOpenDinheiro();
  };

  const onPix = async () => {
    if (!await prepararOperacaoPagamento('pix')) {
      return;
    }

    onOpenPix();
  };

  const onCartao = async () => {
    if (!await prepararOperacaoPagamento('cartao')) {
      return;
    }

    onOpenCartao();
  };

  const isDevolucao = [
    PedidoStatusCode.Finalizado,
    PedidoStatusCode.DevolvidoParcialmente,
  ].includes(pedido.status);

  const paymentDisabledReason = "Reserva expirada. Gere uma nova reserva para continuar.";
  const onLinkPagamento = async () => {
    if (!await prepararOperacaoPagamento('link')) {
      return;
    }

    window.location.assign(`/pagamento/${id}/link-webpag`);
  };
  const onPos = async () => {
    if (!await prepararOperacaoPagamento('pos')) {
      return;
    }

    window.location.assign(`/pagamento/${id}/pos`);
  };

  const handleOrdemTrabalhoChange = (ordemTrabalho: string) => {
    if (pedido.id) {
      updateOrdemTrabalho({
        pedidoId: pedido.id,
        ordemTrabalho
      });
    }
  };

  return (
    <FormProvider {...form}>
      <Portal>
        {isAutoCancelling && <Loading />}
        <ModalCancelar
          isOpen={isOpen}
          onClose={onClose}
          pedido={pedido}
          bilhetes={data.bilhetes}
        />
        <PagamentoDinheiro
          isOpen={isOpenDinheiro}
          onClose={onCloseDinheiro}
          pedido={pedido}
          isPaymentExpired={reservaCountdown.isExpired || !checkoutEffectsAllowed}
        />
        <PagamentoCartao
          isOpen={isOpenCartao}
          onClose={onCloseCartao}
          pedido={pedido}
          numberOfParcela={data.emissor.number_of_parcela}
          isPaymentExpired={reservaCountdown.isExpired || !checkoutEffectsAllowed}
        />
        <PagamentoPix
          isOpen={isOpenPix}
          onClose={onClosePix}
          pedido={pedido}
          isPaymentExpired={reservaCountdown.isExpired || !checkoutEffectsAllowed}
        />
      </Portal>
      <Box
        display={"flex"}
        flexDir={"column"}
        justifyContent={"center"}
        width={"full"}
        alignItems={"center"}
      >
        <Flex
          maxWidth={"140rem"}
          width={"100%"}
          flexDir={"column"}
          justifyContent={"center"}
        >
          <Header />
          <Box
            px={"1rem"}
            bg={"white"}
            w={"full"}
            display={"flex"}
            flexDir={"column"}
            gap={"8"}
            pb={8}
          >
            <Flex
              width={"100%"}
              alignItems={{ base: "stretch", md: "center" }}
              justifyContent={"space-between"}
              flexDirection={{ base: "column", md: "row" }}
              gap={4}
            >
              <Box flex={1}>
                <Limites />
              </Box>
              <ReservaCountdown
                deadline={reservaDeadline}
                remainingSeconds={reservaCountdown.remainingSeconds}
                isExpired={reservaCountdown.isExpired}
              />
            </Flex>
            {[PedidoStatusCode.Cancelado, PedidoStatusCode.EstornoPendente, PedidoStatusCode.Devolvido, PedidoStatusCode.Contestado].includes(pedido.status) === false && (
                <Actions
                handlePix={() => onPix()}
                handleDinheiro={() => onDinheiro()}
                handleCartao={() => onCartao()}
                handleLinkPagamento={() => onLinkPagamento()}
                handlePos={() => onPos()}
                handleCancel={() => onCancel()}
                isDevolucao={isDevolucao}
                showDinheiro={data.emissor.pagamento_faturado_habilitado !== false}
                showPix={data.emissor.pagamento_pix_habilitado !== false}
                showCartao={data.emissor.pagamento_cartao_credito_habilitado !== false}
                showLinkPagamento={!!data.emissor.webpag_link_pagamento_habilitado}
                showPos={!!data.emissor.webpag_pos_habilitado}
                showCancel={canCancelPedido && (!isDevolucao || (!bloqueioDevolucaoAtivo && !bloqueioDevolucaoPresencial))}
                isPaymentDisabled={reservaCountdown.isExpired || pendenciaFinanceira}
                isCancelDisabled={reservaCountdown.isExpired}
                paymentDisabledReason={pendenciaFinanceira ? MENSAGEM_PENDENCIA_FINANCEIRA : paymentDisabledReason}
                selectedPaymentMethod={pedido.forma_pagamento}
              />
            )}
            <Flex gap={2} width={"100%"} minW={"400px"}>
              <Emissor emissor={data.emissor} />
              <Pedido 
                pedido={pedido} 
                emissor={data.emissor}
                onOrdemTrabalhoChange={handleOrdemTrabalhoChange}
                isUpdating={isUpdating}
              />
            </Flex>
            <Bilhetes bilhetes={data.bilhetes} />
          </Box>
          <Footer />
        </Flex>
      </Box>
    </FormProvider>
  );
}
